import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('Jamaica');
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [deliveryProvider, setDeliveryProvider] = useState('');
  const [courierProvider, setCourierProvider] = useState('');
  const [notes, setNotes] = useState('');
  const [savingPreference, setSavingPreference] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [requestingRefund, setRequestingRefund] = useState(false);

  const sessionId = searchParams.get('session_id');
  const paypalOrderId = searchParams.get('paypal_order_id');

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (!sessionId && !paypalOrderId) {
      navigate('/');
      return;
    }

    const checkPaymentStatus = async (attempts = 0) => {
      if (attempts >= 10) {
        setStatus('timeout');
        return;
      }

      try {
        if (paypalOrderId) {
          const response = await axios.get(`${API}/paypal/status/${paypalOrderId}`);
          if (response.data.payment_status === 'paid' || response.data.status === 'confirmed' || response.data.status === 'success') {
            setStatus('success');
            setOrderDetails(response.data);
            await fetchCart();
            return;
          }
        } else {
          if (!token) {
            setStatus('error');
            return;
          }
          const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data.payment_status === 'paid') {
            setStatus('success');
            setOrderDetails(response.data);
            await fetchCart(); // Clear cart
            return;
          } else if (response.data.status === 'expired') {
            setStatus('expired');
            return;
          }
        }

        // Continue polling
        setTimeout(() => checkPaymentStatus(attempts + 1), 2000);
      } catch (error) {
        console.error('Error checking payment:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, paypalOrderId, token, navigate, fetchCart]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="checkout-processing">
        <div className="spinner mb-6"></div>
        <p className="text-leather-600">Confirming your payment...</p>
      </div>
    );
  }

  if (status === 'success') {
    const orderReference = paypalOrderId || sessionId || 'N/A';
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="checkout-success">
        <div className="max-w-xl text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl text-leather-900 mb-4">Thank You!</h1>
          <p className="text-leather-600 mb-8">
            Your order has been placed successfully. You'll receive a confirmation email shortly with your receipt and details.
          </p>
          {orderDetails && (
            <div className="bg-cream-100 p-6 mb-8 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-leather-600">Amount Paid</span>
                <span className="font-medium">${(orderDetails.amount_total / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-leather-600">Currency</span>
                <span className="font-medium uppercase">{orderDetails.currency}</span>
              </div>
            </div>
          )}

          <div className="bg-white border border-cream-200 p-6 mb-8 text-left">
            <h3 className="font-heading text-lg text-leather-900 mb-4">Delivery Preference</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-leather-700">Confirmation Email</label>
                <input
                  className="input mt-1"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm text-leather-700">Country</label>
                <div className="mt-2 space-y-2 text-sm text-leather-700">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="country"
                      value="Jamaica"
                      checked={country === 'Jamaica'}
                      onChange={(e) => setCountry(e.target.value)}
                    />
                    Jamaica
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="country"
                      value="International"
                      checked={country === 'International'}
                      onChange={(e) => setCountry(e.target.value)}
                    />
                    United States & Other Countries
                  </label>
                </div>
              </div>

              {country === 'Jamaica' && (
                <div className="space-y-3">
                  <label className="flex items-start gap-3 text-sm text-leather-700">
                    <input
                      type="radio"
                      name="delivery"
                      value="pickup"
                      checked={deliveryMethod === 'pickup'}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                    />
                    Free pickup (Portmore, St. Catherine or Kingston / Half Way Tree)
                  </label>
                  {deliveryMethod === 'pickup' && (
                    <select
                      className="input"
                      value={pickupLocation}
                      onChange={(e) => setPickupLocation(e.target.value)}
                    >
                      <option value="">Select pickup location</option>
                      <option value="Portmore, St. Catherine">Portmore, St. Catherine</option>
                      <option value="Kingston / Half Way Tree">Kingston / Half Way Tree</option>
                    </select>
                  )}

                  <label className="flex items-start gap-3 text-sm text-leather-700">
                    <input
                      type="radio"
                      name="delivery"
                      value="paid_delivery"
                      checked={deliveryMethod === 'paid_delivery'}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                    />
                    Paid delivery (e.g., Knutsford or similar provider)
                  </label>
                  {deliveryMethod === 'paid_delivery' && (
                    <input
                      className="input"
                      placeholder="Delivery provider (e.g., Knutsford)"
                      value={deliveryProvider}
                      onChange={(e) => setDeliveryProvider(e.target.value)}
                    />
                  )}
                </div>
              )}

              {country === 'International' && (
                <div className="space-y-2 text-sm text-leather-700">
                  <label className="block">Preferred courier provider</label>
                  <input
                    className="input"
                    placeholder="Courier provider (DHL, FedEx, UPS, etc.)"
                    value={courierProvider}
                    onChange={(e) => setCourierProvider(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="text-sm text-leather-700">Notes (optional)</label>
                <textarea
                  className="input mt-1"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any delivery notes"
                />
              </div>
            </div>

            <button
              onClick={async () => {
                if (!email) {
                  toast.error('Please enter an email for confirmation.');
                  return;
                }
                if (!country || !deliveryMethod && country === 'Jamaica') {
                  toast.error('Please select a delivery option.');
                  return;
                }
                if (country === 'Jamaica' && deliveryMethod === 'pickup' && !pickupLocation) {
                  toast.error('Please select a pickup location.');
                  return;
                }
                if (country === 'Jamaica' && deliveryMethod === 'paid_delivery' && !deliveryProvider) {
                  toast.error('Please enter a delivery provider.');
                  return;
                }
                if (country === 'International' && !courierProvider) {
                  toast.error('Please enter a courier provider.');
                  return;
                }

                setSavingPreference(true);
                try {
                  await axios.post(`${API}/checkout/confirmation`, {
                    email,
                    order_id: sessionId,
                    paypal_order_id: paypalOrderId,
                    country,
                    delivery_method: country === 'Jamaica' ? deliveryMethod : 'international_courier',
                    pickup_location: pickupLocation || undefined,
                    delivery_provider: deliveryProvider || undefined,
                    courier_provider: courierProvider || undefined,
                    notes: notes || undefined
                  });
                  localStorage.setItem('deliveryPreference', JSON.stringify({
                    country,
                    deliveryMethod,
                    pickupLocation,
                    deliveryProvider,
                    courierProvider,
                    notes
                  }));
                  toast.success('Delivery preference saved and confirmation email queued.');
                } catch (error) {
                  console.error('Delivery preference error:', error);
                  toast.error('Failed to save preference. Please try again.');
                } finally {
                  setSavingPreference(false);
                }
              }}
              className="mt-4 w-full btn-primary"
              disabled={savingPreference}
            >
              {savingPreference ? 'Saving...' : 'Save Delivery Preference'}
            </button>
          </div>

          <div className="bg-cream-100 p-6 mb-8 text-left">
            <h3 className="font-heading text-lg text-leather-900 mb-2">Refund Policy</h3>
            <p className="text-sm text-leather-700">
              Requests must be submitted within 6 hours of checkout. Approved refunds are processed within 7 business days
              (timing depends on your financial provider).
            </p>
            <div className="mt-4 space-y-2">
              <input
                className="input"
                placeholder="Reason for refund (optional)"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              />
              <button
                className="btn-secondary w-full"
                onClick={async () => {
                  if (!email) {
                    toast.error('Please enter your email above before requesting a refund.');
                    return;
                  }
                  setRequestingRefund(true);
                  try {
                    await axios.post(`${API}/refund/request`, {
                      email,
                      order_id: sessionId,
                      paypal_order_id: paypalOrderId,
                      reason: refundReason || undefined
                    });
                    toast.success('Refund request submitted.');
                  } catch (error) {
                    console.error('Refund request error:', error);
                    toast.error('Failed to submit refund request.');
                  } finally {
                    setRequestingRefund(false);
                  }
                }}
                disabled={requestingRefund}
              >
                {requestingRefund ? 'Submitting...' : `Request Refund for Order ${orderReference}`}
              </button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/orders" className="btn-primary flex items-center justify-center gap-2">
              <Package className="w-4 h-4" /> View Orders
            </Link>
            <Link to="/" className="btn-secondary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="checkout-error">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-3xl text-leather-900 mb-4">
          {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
        </h1>
        <p className="text-leather-600 mb-8">
          {status === 'expired'
            ? 'Your payment session has expired. Please try again.'
            : 'We couldn\'t verify your payment. Please contact support if you were charged.'}
        </p>
        <Link to="/cart" className="btn-primary">
          Return to Cart
        </Link>
      </div>
    </div>
  );
}
